import type { IntelData } from "./translate";

export type CommandCenterActionabilityState = "tradable" | "watch" | "blocked";

export interface CommandCenterActionability {
  state: CommandCenterActionabilityState;
  label: string;
  reason: string;
  blockers: string[];
}

function hasActionableDecision(decision: string): boolean {
  return ["BUY", "SELL", "BULLISH", "BEARISH"].includes(decision);
}

function hasTradeLevels(data: IntelData | undefined): boolean {
  return typeof data?.entry === "number" && typeof data?.stop_loss === "number" && typeof data?.tp1 === "number";
}

function strategyAgent(data: IntelData | undefined): string | undefined {
  const agent = data?.agent?.toLowerCase();
  return agent === "orb" || agent === "fbd" || agent === "mr" ? agent : undefined;
}

function isLongDecision(decision: string): boolean {
  return ["BUY", "BULLISH"].includes(decision);
}

function isShortDecision(decision: string): boolean {
  return ["SELL", "BEARISH"].includes(decision);
}

export function buildCommandCenterActionability(data: IntelData | undefined, decision: string): CommandCenterActionability {
  if (!data || data.data_status === "missing") {
    return {
      state: "blocked",
      label: "Blocked",
      reason: "No live data",
      blockers: ["missing data"],
    };
  }

  const blockers: string[] = [];
  if (data.data_status === "stale") blockers.push("snapshot stale");
  if (data.current_price_disagreement === true) blockers.push("price sources disagree");
  if (data.current_price_status === "stale") blockers.push("price stale");
  if (data.current_price_status === "missing") blockers.push("price missing");
  if (data.order_flow_status === "stale") blockers.push("order flow stale");
  if (data.volume_flow_status === "stale") blockers.push("volume flow stale");

  if (blockers.length > 0) {
    return {
      state: "blocked",
      label: "Blocked",
      reason: blockers.slice(0, 2).join(" · "),
      blockers,
    };
  }

  if (!hasActionableDecision(decision)) {
    return {
      state: "watch",
      label: "Watch",
      reason: "No actionable edge yet",
      blockers: [],
    };
  }

  const agent = strategyAgent(data);
  if (agent === "fbd" && isShortDecision(decision)) {
    return {
      state: "blocked",
      label: "Blocked",
      reason: "FBD is long-only",
      blockers: ["strategy direction mismatch"],
    };
  }

  if (agent === "mr" && isLongDecision(decision)) {
    return {
      state: "blocked",
      label: "Blocked",
      reason: "MR is short-only",
      blockers: ["strategy direction mismatch"],
    };
  }

  if (!hasTradeLevels(data)) {
    return {
      state: "watch",
      label: "Watch",
      reason: "Awaiting entry/SL/TP",
      blockers: ["levels missing"],
    };
  }

  if (!agent) {
    return {
      state: "watch",
      label: "Watch",
      reason: "Unqualified trade levels",
      blockers: ["strategy source missing"],
    };
  }

  return {
    state: "tradable",
    label: "Tradable",
    reason: "Fresh data + actionable levels",
    blockers: [],
  };
}
